import { useEffect, useMemo, useState } from 'react';

import axios from 'axios';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { createContainer, useContainer } from 'unstated-next';

import AdminAuthContainer from './AdminAuthContainer';
import {
    DELETE_LTI_CREDENTIAL,
    GET_LTI_CREDENTIAL_LIST,
    REGENERATE_SHARED_SECRET,
    UPSERT_LTI_CREDENTIAL
} from '../../config/apiPaths';
import { useApiEndpointGet } from '../../hooks/useApiEndpointGet';
import { type LTICredential } from '../../types/apiTypes';
import { getUserUuidHeaders } from '../../utils/httpUtils';
import { getUrls } from '../../utils/url';


export enum IntegrationsView {
    List = 0,
    Edit = 1,
    Create = 2
}

export enum LTIVersion {
    V1_1 = '1.1',
    V1_3 = '1.3',
    NONE = 'none'
}

type IntegrationsRouteParams = {
    action?: string;
    integration?: string;
};

interface AdminIntegrations {
    isLoading: boolean;
    fetchIntegrationList: () => void;
    integrations: LTICredential[];
    integrationsFetchStatus: boolean;
    integration: LTICredential | undefined;
    updateIntegration: (recordData: Partial<LTICredential>) => Promise<LTICredential | undefined>;
    createIntegration: (recordData: Partial<LTICredential>) => Promise<LTICredential | undefined>;
    deleteSelectedIntegration: () => Promise<void>;
    regenerateSecret: () => Promise<LTICredential | undefined>;
    uuid: string;
    showListView: () => void;
    showEditView: (integrationId: string) => void;
    view: IntegrationsView;
} 

const useAdminIntegrations = (): AdminIntegrations => {
    const { uuid, isLoading: isLoadingAuth } = useContainer(AdminAuthContainer);
    const { api } = getUrls();
    const { t } = useTranslation('admin_settings');
    const match = useRouteMatch<IntegrationsRouteParams>("/adminSettings/integrations/:action?/:integration?");
    const path = match?.path;
    const params = match?.params;
    const [view, setView] = useState<IntegrationsView>(IntegrationsView.List);        
    const [integration, setIntegration] = useState<LTICredential | undefined>(undefined);
    useEffect(()=> {
        if (params?.action === 'edit') {
            setView(IntegrationsView.Edit);
        } else if (params?.action === 'create') {
            setView(IntegrationsView.Create);
        } else {
            setView(IntegrationsView.List);
        }
    }, [path, params])


    const history = useHistory();
    const { enqueueSnackbar } = useSnackbar();
    const { data: integrations = [], isLoading: isLoadingIntegrations, refetch: fetchIntegrationList } =
        useApiEndpointGet<LTICredential[]>({
            userUuid: uuid,
            apiPath: GET_LTI_CREDENTIAL_LIST,
            enabled: !!uuid && !isLoadingAuth,
            useQueryOptions: {
                onError: () => {
                    enqueueSnackbar(t('integration-error.fetch-all-credentials'));
                }
            }
        });
    const showListView = () => { 
        // remove everyting after integations in the path
        // and history.replace with that path
        setIntegration(undefined);
        history.replace("/adminSettings/integrations");
    }

    const showEditView = (integrationId: string) => {
        // remove everyting after integations in the path
        // and history.replace with that path plus /edit/integrationId
        history.replace(`/adminSettings/integrations/edit/${integrationId}`);        
    }
    const isLoading = useMemo(() => isLoadingAuth || isLoadingIntegrations, [isLoadingAuth, isLoadingIntegrations]);
    useEffect(() => {
        if (isLoading) {
            return;
        }
        if (params?.integration) {
            const selectedIntegration = integrations.find((integration) => integration.lti_creds_uuid === params.integration);
            if (!selectedIntegration) {
                return;
            }
            setIntegration(selectedIntegration);
        } else {
            setIntegration(undefined);
        }
    }, [isLoading, params, integrations, enqueueSnackbar, t]);



    const updateIntegration = async (recordData: Partial<LTICredential>) => {
        try {
            const { data } = await axios.post<LTICredential>(`${api}${UPSERT_LTI_CREDENTIAL}`, {
                ...recordData,
                user_uuid: uuid,
                learn_app_key: recordData.learn_app_key || null,
                learn_app_secret: recordData.learn_app_secret || null
            });
            const successMessage = t('integration-success.update', { title: recordData.title });
            enqueueSnackbar(successMessage, { variant: 'success' });
            await fetchIntegrationList();
            return data;
        } catch (err) {
            console.log(err);
            enqueueSnackbar(t('integration-error.update-integration'));
        }
    };

    const createIntegration = async (recordData: Partial<LTICredential>): Promise<LTICredential|undefined> => {
        try {
            const { data } = await axios.post<LTICredential>(`${api}${UPSERT_LTI_CREDENTIAL}`, {
                ...recordData,
                user_uuid: uuid,
                learn_app_key: recordData.learn_app_key || null,
                learn_app_secret: recordData.learn_app_secret || null
            });
            const successMessage = t('integration-success.create', { title: recordData.title });
            enqueueSnackbar(successMessage, { variant: 'success' });
            await fetchIntegrationList();
            return data;
        } catch (err) {
            console.log(err);
            enqueueSnackbar(t('integration-error.create-integration'));
        }
        return undefined;
    };

    const regenerateSecret = async (): Promise<LTICredential | undefined> => {
        if (!integration) {
            enqueueSnackbar(t('integration-error.regenerate-secret'));
            return;
        }
        try {
            const { data } = await axios.post(`${api}${REGENERATE_SHARED_SECRET}`, {
                lti_creds_uuid: integration.lti_creds_uuid,
                user_uuid: uuid
            });
            await fetchIntegrationList();
            return data;
        } catch (err) {
            console.log(err);
            enqueueSnackbar(t('integration-error.regenerate-secret'));
        }
        return undefined;
    };

    const deleteIntegration = async () => {
        if (!integration) {
            enqueueSnackbar(t('integration-error.delete-integration'));
            return;
        }
        try {
            const { data } = await axios.delete(
                `${api}${DELETE_LTI_CREDENTIAL}?lti_creds_uuid=${integration.lti_creds_uuid}`,
                { headers: getUserUuidHeaders(uuid) }
            );
            await fetchIntegrationList();
        } catch (err) {
            console.log(err);
            enqueueSnackbar(t('integration-error.delete-integration'));
        }

    };

    return {
        isLoading,
        fetchIntegrationList,
        integrations,
        integrationsFetchStatus: isLoadingIntegrations,
        integration,
        updateIntegration,
        createIntegration,
        deleteSelectedIntegration: deleteIntegration,
        regenerateSecret,
        uuid,
        showListView,
        showEditView,
        view
    };
};

const AdminIntegrationsContainer = createContainer(useAdminIntegrations);

export default AdminIntegrationsContainer;
